import { AnyAction } from 'redux'
import actionTypes from '../actions/actionTypes'
import { IActionWindowResized } from '../../redux/actions/appActions'
import sessionTypes from '../../helpers/enums/sessionTypes'
import { ICharacterData } from '../../helpers/data/characterData'
import { getAvatarName } from '../../helpers/data/avatarNames'
import { jwtDecode } from 'jwt-decode'
import { ISize } from '../../helpers/data/size'
import { ISkin as IWaitingSkin } from '../../views/waiting/Waiting'
import { ISkin as IGameSkin } from '../../views/game/Game'
import { ISkin as ILoginSkin } from '../../views/login/Login'
import { ISkin as IWinnersSkin } from '../../views/winners/Winners'
import { ISkin as IMainSkin } from '../../helpers/data/mainSkinData'
import { defaultAsset } from '../../components/asset/Asset'
import settingsEnum from '../../helpers/enums/settings'

interface IAudio {
	volume: number
	isMuted: boolean
}

interface IFeatureFlags {
	aprilFoolsSkin: boolean
	halloweenSkin: boolean
	holidaySkin: boolean
	underwaterSkin: boolean
	valentinesSkin: boolean
	teacherDisableTTCTimer: boolean
}

interface IConfig {
	_loaded: boolean
	assets_path: string
}

const defaultGameSkin = {
	leaderboard: {
		backgroundColor: '',
		player: {
			color: '',
		},
	},
	audio: {
		path: '',
	},
	dashboard: {
		background: defaultAsset,
		backgroundGradient: defaultAsset,
		celebration: [],
		miscellaneous: [],
	},
	countdown: {
		background: defaultAsset,
		animation: defaultAsset,
	},
	questions: {
		elements: [],
		answers: [],
		particleSystems: null,
	},
	progressBar: {
		color: '',
	},
	topInformation: {
		backgroundColor: '',
	},
}

const defaultLoginSkin = {
	background: defaultAsset,
	bottomBar: {
		backgroundColor: '',
	},
}

const defaultWinnersSkin = {
	podium: {
		background: defaultAsset,
		podium: defaultAsset,
		title: defaultAsset,
	},
	plantFlag: {
		background: defaultAsset,
		animation: defaultAsset,
		message: {
			position: 'top',
			color: '',
		},
		progressBar: {
			color: '',
		},
	},
}

const defaultMainSkin = {
	fonts: [],
}

interface ISkin {
	_loaded: boolean
	waiting: IWaitingSkin
	game: IGameSkin
	login: ILoginSkin
	winners: IWinnersSkin
	main: IMainSkin
	characters: ICharacterData[]
}

interface IAnimationsToggle {
	enabled: boolean
	hasBeenToggled: boolean
}

export interface IAppStore {
	size: ISize
	isSplashComplete: boolean
	isConnected: boolean
	isTeacher: boolean
	playerUID: string
	sessionType: number | string
	audio: IAudio
	config: IConfig
	skin: ISkin
	projectorMode: boolean
	canParticipate: boolean
	userInteracted: boolean
	featureFlags: IFeatureFlags
	animationsToggle: IAnimationsToggle
}

const defaultState = {
	size: {
		width: 0,
		height: 0,
	},
	isSplashComplete: false,
	isConnected: false,
	isTeacher: true,
	playerUID: '',
	sessionType: '',
	audio: {
		volume: settingsEnum.SETTING_GAME_AUDIO_DEFAULT_VOLUME_VALUE,
		isMuted: true,
	},
	config: {
		_loaded: false,
		assets_path: '',
	},
	skin: {
		_loaded: false,
		waiting: {
			background: defaultAsset,
			bottomBar: {
				backgroundColor: '',
				buttonBackgroundColor: '',
			},
		},
		game: defaultGameSkin,
		login: defaultLoginSkin,
		winners: defaultWinnersSkin,
		main: defaultMainSkin,
		characters: [],
	},
	projectorMode: false,
	canParticipate: true,
	userInteracted: false,
	featureFlags: {
		aprilFoolsSkin: false,
		halloweenSkin: false,
		holidaySkin: false,
		underwaterSkin: false,
		teacherDisableTTCTimer: false,
		valentinesSkin: false,
	},
	animationsToggle: {
		enabled: true,
		hasBeenToggled: false,
	},
}

const onWindowResize = (state: IAppStore, action: IActionWindowResized): IAppStore => {
	return {
		...state,
		size: {
			...state.size,
			width: action.width,
			height: action.height,
		},
	}
}

const removeSplash = (state: IAppStore, _action: AnyAction): IAppStore => {
	return {
		...state,
		isSplashComplete: true,
	}
}

const initializeApp = (state: IAppStore, action: AnyAction): IAppStore => {
	let decodedJwt: any
	try {
		decodedJwt = jwtDecode(action.params.jwt)
	} catch (err) {
		decodedJwt = {
			uid: '1',
			isTeacher: '0',
			sessionType: sessionTypes.LIVE_SESSION,
			extra: {
				session_type: '0',
			},
		}
	}

	//TODO: remove this
	// decodedJwt.extra.session_type = sessionTypes.EMBED_NUMBER

	return {
		...state,
		playerUID: decodedJwt.uid,
		isTeacher: decodedJwt.isTeacher === '1',
		sessionType: decodedJwt.extra.session_type || sessionTypes.LIVE_SESSION,
		projectorMode: decodedJwt.extra.pm === '1',
	}
}

const handleConfigLoaded = (state: IAppStore, action: AnyAction): IAppStore => {
	return {
		...state,
		config: {
			...action.config,
			_loaded: true,
		},
	}
}

const handleAppConnected = (state: IAppStore, _action: AnyAction): IAppStore => {
	return {
		...state,
		isConnected: true,
	}
}

const updateSkin = (state: IAppStore, action: AnyAction): IAppStore => {
	const enrichedCharacters = action.data.characters?.map((character: ICharacterData) => ({
		...character,
		name: getAvatarName(character.id),
	})) || []

	return {
		...state,
		skin: {
			...action.data,
			characters: enrichedCharacters,
			_loaded: true,
		},
	}
}

const updateHideStudentNames = (state: IAppStore, action: AnyAction): IAppStore => {
	return {
		...state,
		projectorMode: action.hideStudentNames,
	}
}

const handleLimitReached = (state: IAppStore, _action: AnyAction): IAppStore => {
	return {
		...state,
		canParticipate: false,
	}
}

function setFeatureFlags(state: IAppStore = defaultState, action: AnyAction) {
	return {
		...state,
		featureFlags: action.featureFlags,
	}
}

function updateAudio(state: IAppStore = defaultState, action: AnyAction) {
	return {
		...state,
		audio: {
			volume: action.volume,
			isMuted: action.isMuted,
		},
	}
}

function userInteractionUpdate(state: IAppStore = defaultState, _action: AnyAction) {
	return {
		...state,
		userInteracted: true,
		audio: {
			...state.audio,
			volume: settingsEnum.SETTING_GAME_AUDIO_DEFAULT_VOLUME_VALUE,
			isMuted: false,
		},
	}
}

function animationsToggleUpdate(state: IAppStore = defaultState, action: AnyAction) {
	return {
		...state,
		animationsToggle: {
			enabled: action.payload.enabled,
			hasBeenToggled: true,
		},
	}
}

function viewsReducer(state: IAppStore = defaultState, action: AnyAction): IAppStore {
	switch (action.type) {
		case actionTypes.APP_WINDOW_RESIZE:
			return onWindowResize(state, action as IActionWindowResized)
		case actionTypes.APP_INIT:
			return initializeApp(state, action)
		case actionTypes.APP_CONFIG_LOADED:
			return handleConfigLoaded(state, action)
		case actionTypes.APP_CONNECTED:
			return handleAppConnected(state, action)
		case actionTypes.SPLASH_COMPLETE:
			return removeSplash(state, action)
		case actionTypes.APP_SKIN_LOADED:
			return updateSkin(state, action)
		case actionTypes.SESSION_SETTINGS_HIDE_STUDENT_NAMES_UPDATE:
			return updateHideStudentNames(state, action)
		case actionTypes.APP_LIMIT_REACHED:
			return handleLimitReached(state, action)
		case actionTypes.FEATURE_FLAGS_GET_SUCCESS:
			return setFeatureFlags(state, action)
		case actionTypes.AUDIO_UPDATE:
			return updateAudio(state, action)
		case actionTypes.USER_INTERACTION_UPDATE:
			return userInteractionUpdate(state, action)
		case actionTypes.ANIMATIONS_TOGGLE_UPDATE:
			return animationsToggleUpdate(state, action)
		default:
			return state
	}
}

export default viewsReducer
